// ===========================================
// BUSTED! BLACKJACK - LOBBY PAGE
// ===========================================

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSocketStore, useAuthStore } from '../stores';
import { onReconnect } from '../stores/socketStore';
import { useHealthCheck } from '../hooks/useHealthCheck';
import { formatBalance } from '../utils/format';
import { BahtSymbol } from '../components';
import { LoadingVideo } from '../components/LoadingVideo';
import bustedLogo from '../assets/busted.png';

// Animated dots for "reconnecting" text
function AnimatedDots() {
  const [dotCount, setDotCount] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setDotCount((prev) => (prev + 1) % 4);
    }, 400);
    return () => clearInterval(interval);
  }, []);

  return (
    <span className="inline-block w-[12px] text-left">
      {'.'.repeat(dotCount)}
    </span>
  );
}

interface TableSummary {
  id: string;
  name: string;
  minBalance: number;
  playerCount: number;
  maxPlayers: number;
  phase: string;
  spectatorCount: number;
  dealerName: string;
}

interface TierSummary {
  minBalance: number;
  tableCount: number;
  totalPlayers: number;
  totalSpectators: number;
  tables: TableSummary[];
}

export function LobbyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { socket, isConnected, isReconnecting, reconnectAttempt, connectionError, forceReconnect } = useSocketStore();
  const { user } = useAuthStore();
  const [tiers, setTiers] = useState<TierSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [joiningTier, setJoiningTier] = useState<number | null>(null);
  const [isQuickJoining, setIsQuickJoining] = useState(false);
  const [isJoiningMyTable, setIsJoiningMyTable] = useState(false);
  const [expandedTiers, setExpandedTiers] = useState<Set<number>>(new Set());
  const [myTableInfo, setMyTableInfo] = useState<{
    tableSlug: string | null;
    exists: boolean;
    playerCount: number;
    maxPlayers: number;
  } | null>(null);

  // Track pending join to prevent duplicate navigations
  const pendingJoinRef = useRef<number | null>(null);

  // Health check polling - auto-reconnect when server becomes available
  useHealthCheck({
    enabled: !isConnected,
    onHealthy: () => console.log('[LobbyPage] Server healthy, reconnecting...'),
  });

  // Check for kick message from navigation state
  const kickMessage = (location.state as { kickMessage?: string })?.kickMessage;
  const [showKickMessage, setShowKickMessage] = useState(!!kickMessage);

  // Auto-hide kick message after 5 seconds
  useEffect(() => {
    if (showKickMessage) {
      const timer = setTimeout(() => setShowKickMessage(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [showKickMessage]);

  // Subscribe to reconnection events to re-request data
  useEffect(() => {
    const unsubscribe = onReconnect(() => {
      console.log('[LobbyPage] Reconnection detected, re-requesting data');
      const currentSocket = useSocketStore.getState().socket;
      if (currentSocket) {
        setIsLoading(true);
        currentSocket.emit('client_message', { type: 'get_tables' });
        currentSocket.emit('client_message', { type: 'get_my_table' });
      }
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    if (!socket || !isConnected) return;

    // Request table list and my table info
    socket.emit('client_message', { type: 'get_tables' });
    socket.emit('client_message', { type: 'get_my_table' });

    // Listen for messages
    const handleMessage = (message: any) => {
      if (message.type === 'table_list') {
        setTiers(message.tiers || []);
        setIsLoading(false);
      }

      if (message.type === 'my_table_info') {
        setMyTableInfo({
          tableSlug: message.tableSlug,
          exists: message.exists,
          playerCount: message.playerCount,
          maxPlayers: message.maxPlayers,
        });
      }

      // Handle successful join - navigate to table
      if (message.type === 'table_joined' && (pendingJoinRef.current !== null || isQuickJoining || isJoiningMyTable)) {
        const tableId = message.table?.id;
        const ownerSlug = message.table?.ownerSlug;
        if (tableId) {
          pendingJoinRef.current = null;
          setJoiningTier(null);
          setIsQuickJoining(false);
          setIsJoiningMyTable(false);
          // Use slug for personal tables, tableId for tier-based tables
          const urlPath = ownerSlug ? `/table/${ownerSlug}` : `/table/${tableId}`;
          navigate(urlPath);
        }
      }

      // Handle join error
      if (message.type === 'error' && (pendingJoinRef.current !== null || isQuickJoining || isJoiningMyTable)) {
        pendingJoinRef.current = null;
        setJoiningTier(null);
        setIsQuickJoining(false);
        setIsJoiningMyTable(false);
      }
    };

    socket.on('server_message', handleMessage);

    return () => {
      socket.off('server_message', handleMessage);
    };
  }, [socket, isConnected, navigate, isQuickJoining, isJoiningMyTable]);

  const handleQuickJoin = () => {
    if (!socket || isQuickJoining || joiningTier !== null || isJoiningMyTable) return;

    setIsQuickJoining(true);
    socket.emit('client_message', { type: 'quick_join' });
  };

  const handleJoinMyTable = () => {
    if (!socket || isJoiningMyTable || isQuickJoining || joiningTier !== null) return;

    setIsJoiningMyTable(true);
    socket.emit('client_message', { type: 'join_my_table' });
  };

  const handleJoinTier = (minBalance: number) => {
    if (!socket || joiningTier !== null || isQuickJoining || isJoiningMyTable) return;

    setJoiningTier(minBalance);
    pendingJoinRef.current = minBalance;

    // Send join request with minBalance (tier-based join)
    socket.emit('client_message', {
      type: 'join_table',
      minBalance,
      mode: 'play',
    });
  };

  const handleSpectateTable = (tableId: string) => {
    navigate(`/table/${tableId}?mode=spectate`);
  };

  const toggleTierExpand = (minBalance: number) => {
    setExpandedTiers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(minBalance)) {
        newSet.delete(minBalance);
      } else {
        newSet.add(minBalance);
      }
      return newSet;
    });
  };

  if (!isConnected) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          {isReconnecting ? (
            <>
              <div className="text-yellow-400 text-xl mb-2">
                Reconnecting to server<AnimatedDots />
              </div>
              <div className="text-gray-400 text-sm">
                Attempt {reconnectAttempt}
              </div>
            </>
          ) : connectionError ? (
            <>
              <div className="text-red-400 text-xl mb-2">
                Connection failed
              </div>
              <div className="text-gray-400 text-sm mb-4">
                {connectionError}
              </div>
              <button
                onClick={forceReconnect}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 transition-colors"
              >
                Retry Connection
              </button>
            </>
          ) : (
            // Initial connection - show loading video instead of text
            <LoadingVideo />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          {/* Balance - Left */}
          <div className="px-4 py-2 bg-green-600 rounded-lg">
            <span className="text-white text-xl font-bold">
              {formatBalance(user?.balance ?? 0)}
            </span>
            <span className="text-white text-xl font-normal ml-1">B-Bucks</span>
          </div>

          {/* Logo/Title - Center */}
          <div className="flex items-center gap-3">
            <img src={bustedLogo} alt="BUSTED!" className="h-[70px]" />
            <div>
              <h1 className="text-xl font-bold text-white">Busted Blackjack</h1>
              <p className="text-sm text-gray-400">Casino Accurate Gameplay</p>
            </div>
          </div>

          {/* Back Button - Right */}
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
          >
            Back to Home
          </button>
        </div>

        {/* Kick Message Banner */}
        {showKickMessage && kickMessage && (
          <div className="mb-6 p-4 bg-red-900/80 border border-red-500 rounded-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-red-400 text-xl">⚠️</span>
              <span className="text-red-100">{kickMessage}</span>
            </div>
            <button
              onClick={() => setShowKickMessage(false)}
              className="text-red-300 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>
        )}

        {/* Tiers List - filtered by user balance, sorted by min balance descending */}
        {(() => {
          const qualifyingTiers = tiers
            .filter((tier) => (user?.balance ?? 0) >= tier.minBalance)
            .sort((a, b) => b.minBalance - a.minBalance);

          if (isLoading) {
            return (
              <div className="text-center text-gray-400 py-12">
                Loading tables...
              </div>
            );
          }

          if (tiers.length === 0) {
            return (
              <div className="text-center text-gray-400 py-12">
                No tables available. Check back soon!
              </div>
            );
          }

          if (qualifyingTiers.length === 0) {
            const lowestMinBalance = Math.min(...tiers.map(t => t.minBalance));
            return (
              <div className="text-center text-gray-400 py-12">
                <p>Your balance is too low to join any tables.</p>
                <p className="mt-2">Minimum required: {formatBalance(lowestMinBalance)}</p>
              </div>
            );
          }

          // Calculate total active players across all qualifying tiers
          const totalActivePlayers = qualifyingTiers.reduce((sum, t) => sum + t.totalPlayers, 0);

          return (
            <div className="grid gap-4">
              {/* MY TABLE Card - Blue/Personal */}
              {myTableInfo?.tableSlug && (
                <div className="bg-gradient-to-r from-blue-900/80 to-indigo-800/80 rounded-xl p-4 border-2 border-blue-500/50 shadow-lg shadow-blue-500/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <h2 className="text-xl font-bold text-blue-100 w-[220px]">
                        🏠 MY TABLE
                      </h2>
                      <span className="text-sm text-blue-200/80 w-[120px]">
                        👥 {myTableInfo.playerCount} {myTableInfo.playerCount === 1 ? 'player' : 'players'}
                      </span>
                      <span className="text-sm text-blue-300/60 font-mono">
                        🎰 bustedblackjack.com/table/{myTableInfo.tableSlug}
                      </span>
                    </div>
                    <button
                      onClick={handleJoinMyTable}
                      disabled={isJoiningMyTable || isQuickJoining || joiningTier !== null}
                      className="px-6 py-2 bg-blue-500 text-white font-bold rounded-lg hover:bg-blue-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px]"
                    >
                      {isJoiningMyTable
                        ? 'Joining...'
                        : myTableInfo.playerCount >= myTableInfo.maxPlayers
                          ? 'Spectate'
                          : 'Join'
                      }
                    </button>
                  </div>
                </div>
              )}

              {/* PUBLIC TABLES Section Header */}
              <div className="flex items-baseline gap-2 mt-2">
                <h2 className="text-xl font-bold text-white">PUBLIC TABLES</h2>
                <span className="text-sm text-gray-500">(tiered by minimum available balance)</span>
              </div>

              {/* QUICK JOIN Card - Gold/Highlighted */}
              <div className="bg-gradient-to-r from-yellow-900/80 to-amber-800/80 rounded-xl p-4 border-2 border-yellow-500/50 shadow-lg shadow-yellow-500/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    <h2 className="text-xl font-bold text-yellow-100">
                      ⚡ QUICK JOIN
                    </h2>
                    <span className="text-sm text-yellow-200/80">
                      {totalActivePlayers > 0
                        ? `Join ${totalActivePlayers} active ${totalActivePlayers === 1 ? 'player' : 'players'}`
                        : `Any table. Any tier. Dealer's choice.`
                      }
                    </span>
                  </div>
                  <button
                    onClick={handleQuickJoin}
                    disabled={isQuickJoining || joiningTier !== null || isJoiningMyTable}
                    className="px-6 py-2 bg-yellow-500 text-gray-900 font-bold rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px]"
                  >
                    {isQuickJoining ? 'Joining...' : 'Play Now'}
                  </button>
                </div>
              </div>

              {/* Tier Cards */}
              {qualifyingTiers.map((tier) => (
                <div
                  key={tier.minBalance}
                  className="bg-gray-800 rounded-xl p-4 border border-gray-700"
                >
                  {/* Tier Header */}
                  <div className="flex items-center gap-4">
                    <h2 className="text-xl font-bold text-white whitespace-nowrap">
                      {tier.minBalance < 1000 ? (
                        <><span className="text-gray-400">&lt;</span>{' '}1,000 <BahtSymbol />Players</>
                      ) : (
                        <>{formatBalance(tier.minBalance)} <BahtSymbol />Players</>
                      )}
                    </h2>
                    <div className="flex items-center gap-6 ml-auto text-sm text-gray-400 whitespace-nowrap">
                      <span>
                        👥 {tier.totalPlayers} {tier.totalPlayers === 1 ? 'player' : 'players'}
                      </span>
                      <span>
                        🎰 {tier.tableCount} active {tier.tableCount === 1 ? 'table' : 'tables'}
                      </span>
                    </div>
                    <div className="flex gap-3">
                      {tier.tableCount > 0 && (
                        <button
                          onClick={() => toggleTierExpand(tier.minBalance)}
                          className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-500 transition-colors"
                        >
                          {expandedTiers.has(tier.minBalance) ? 'Hide Tables' : 'View Tables'}
                        </button>
                      )}
                      <button
                        onClick={() => handleJoinTier(tier.minBalance)}
                        disabled={joiningTier !== null || isQuickJoining || isJoiningMyTable}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[120px]"
                      >
                        {joiningTier === tier.minBalance ? 'Joining...' : 'Play'}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Tables List */}
                  {expandedTiers.has(tier.minBalance) && tier.tables.length > 0 && (
                    <div className="mt-4 pl-4 border-l-2 border-gray-700 space-y-2">
                      {tier.tables.map((table) => (
                        <div
                          key={table.id}
                          className="flex items-center justify-between py-2 px-3 bg-gray-700/50 rounded-lg"
                        >
                          <div className="flex items-center gap-4">
                            <span className="text-white font-medium">
                              {table.dealerName}
                            </span>
                            <span className="text-sm text-gray-400">
                              👥 {table.playerCount}/{table.maxPlayers}
                            </span>
                            <span className={`text-sm ${
                              table.phase === 'waiting' || table.phase === 'complete' ? 'text-green-400' :
                              table.phase === 'betting' ? 'text-yellow-400' : 'text-red-400'
                            }`}>
                              {table.phase === 'waiting' && '🟢 Open'}
                              {table.phase === 'betting' && '🟡 Betting'}
                              {table.phase === 'dealing' && '🔴 Dealing'}
                              {table.phase === 'player_turn' && '🔴 In Progress'}
                              {table.phase === 'dealer_turn' && '🔴 Dealer Turn'}
                              {table.phase === 'complete' && '🟢 Round Complete'}
                            </span>
                          </div>
                          <button
                            onClick={() => handleSpectateTable(table.id)}
                            className="px-3 py-1 bg-gray-600 text-white text-sm rounded hover:bg-gray-500 transition-colors"
                          >
                            Watch
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })()}

        {/* Info Box */}
        <div className="mt-8 bg-gray-800 rounded-xl p-6 border border-gray-700">
          <h3 className="text-lg font-semibold text-white mb-3">How to Play</h3>
          <ul className="text-gray-400 space-y-2">
            <li>• Click "Play" to join a table at your desired stake level</li>
            <li>• Click "View Tables" to see active tables and watch games</li>
            <li>• Place your bet when the table is open for betting</li>
            <li>• Beat the dealer without going over 21!</li>
          </ul>
        </div>

        {/* Footer */}
        <footer className="mt-8 py-6 border-t border-gray-800">
          <div className="text-center text-sm text-gray-500">
            BUSTED! Blackjack • <span className="text-gray-400">*</span> Play Money Only • Not Real Gambling • Must be 18+
          </div>
        </footer>
      </div>
    </div>
  );
}
